/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, {
  FC,
  useCallback,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import classNames, { Argument } from "classnames/dedupe";
import Badge, { IBadgeItem } from "../../../DataDisplay/Badge/Badge";
import { useWindowDimensions } from "../../../../hooks";

export interface IImageZoomProps {
  /**
   * Image src param
   */
  img?: string;

  /**
   * List of badges items
   */
  badges?: IBadgeItem[];

  classes?: {
    root?: Argument;
  };

  /**
   * Set translation text for labels
   */
  labels?: {
    helper?: string;
  };

  /**
   * A callback function for when image is clicked by user
   */
  onClick?(event: React.MouseEvent): void;

  /**
   * Custom image to display
   */
  customImage?: JSX.Element;
  /**
   * Image height getter to adjust height for alternative pdp layout carousel
   */
  getMainImageHeight?(height: number): void;
}

const ImageZoom: FC<IImageZoomProps> = ({
  img,
  classes,
  badges,
  labels,
  onClick,
  customImage,
  getMainImageHeight,
}) => {
  const { viewportWidth } = useWindowDimensions();
  const [zoom, setZoom] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(() => ({ x: 0, y: 0 }));

  const imageContainer = useRef(null);

  const handleMouseOver = useCallback(() => {
    setZoom(true);
  }, []);
  const handleMouseOut = useCallback(() => {
    setZoom(false);
  }, []);

  const handleMouseMovement = useCallback((e) => {
    // eslint-disable-next-line prefer-const
    let { left, top, width, height } =
      imageContainer.current.getBoundingClientRect();

    left += window.scrollX;
    top += window.scrollY;

    const x = ((e.pageX - left) / parseInt(width, 10)) * 100;
    const y = ((e.pageY - top) / parseInt(height, 10)) * 100;

    setCursorPosition({
      x,
      y,
    });
  }, []);

  const handleClick = useCallback((e) => onClick?.(e), [onClick]);

  useLayoutEffect(() => {
    const mainImageHeight = document.getElementsByClassName(
      "pdp-main-image__hidden",
    )[0].clientHeight;
    getMainImageHeight?.(mainImageHeight);
  }, [getMainImageHeight, viewportWidth]);

  return (
    <div
      ref={imageContainer}
      className={classNames("pdp-main-image", classes?.root)}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseMove={handleMouseMovement}
      onClick={handleClick}
    >
      {/* Added to calculate the height of main image container */}
      <div className="pdp-main-image__hidden" />
      <div className="product-badges">
        {badges?.map((badge) => (
          <Badge key={badge.color} color={badge.color} text={badge.text} />
        ))}
      </div>
      {labels?.helper && (
        <div className="pdp-help-img">
          <span>{labels?.helper}</span>
        </div>
      )}
      <div
        className="zoom cursor-zoom"
        style={{
          transition: `transform ${0.6}s ease-in`,
          transitionTimingFunction: `cubic-bezier(0.32, -0.08, 0.32, 1.60)`,
          transformOrigin: `${cursorPosition.x}% ${cursorPosition.y}%`,
          transform: zoom ? `scale(${2})` : "scale(1)",
          backgroundSize: "contain",
          ...(!customImage ? { backgroundImage: `url('${img}')` } : {}),
        }}
      >
        {customImage}
      </div>
    </div>
  );
};

export default ImageZoom;
